import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUsers, faTractor } from '@fortawesome/free-solid-svg-icons';
import Circles from '../../../assets/images/circle.png';
import axios from 'axios';

const AtariDashboard = ({ 
  loading, 
  handleMoreInfoClick 
}) => {
  // Get atariCode from session storage
  const atariCode = sessionStorage.getItem("atariCode");

  // Local state for ATARI Admin specific data
  const [userCount, setUserCount] = useState(0);
  const [demonstrationCount, setDemonstrationCount] = useState(0);
  const [localLoading, setLocalLoading] = useState(false);
  const [userDetails, setUserDetails] = useState([]);
  const [demonstrationDetails, setDemonstrationDetails] = useState([]);
  const [activeTable, setActiveTable] = useState('none'); // 'none', 'user', 'demonstration'
  const [drillDownData, setDrillDownData] = useState([]);
  const [showDrillDown, setShowDrillDown] = useState(false);
  const [selectedKvkCode, setSelectedKvkCode] = useState(null);
  const [demoDrillDownData, setDemoDrillDownData] = useState([]);
  const [showDemoDrillDown, setShowDemoDrillDown] = useState(false);
  const [selectedDemoKvkCode, setSelectedDemoKvkCode] = useState(null);

  // Helper function for CSV export
  const exportToCSV = (data, filename, headers) => {
    if (!data || data.length === 0) {
      alert("No data available to export.");
      return;
    }

    const csvRows = [headers.join(",")];

    data.forEach((item, index) => {
      const row = [
        index + 1,
        `"${item.name || item.stateName || item.districtName || item.kvK_Name || item.userRoleName || 'N/A'}"`,
        item.count || item.total || item.totalUsers || 0
      ];
      csvRows.push(row.join(","));
    });

    const csvContent = csvRows.join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `${filename}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  useEffect(() => {
    fetchUserDetails();
    fetchDemonstrationCount();
  }, []);

  // Fetch ATARI Dashboard Report Count
  const fetchAtariDashboardData = async () => {
    try {
      setLocalLoading(true);
      console.log('=== ATARI Dashboard - Fetching Data ===');
      
      const { data } = await axios.post(
        `${process.env.NODE_ENV === "development" ? process.env.REACT_APP_DEV_MODE : process.env.REACT_APP_PRO_MODE}/api/MT/ICAR`,
        {
          "flag": "Atari-1",
          "atariCode": parseInt(atariCode)
        },
        {
          headers: {
            'accept': '*/*',
            'Content-Type': 'application/json'
          }
        }
      );

      console.log('ATARI Dashboard API Response:', data);

      if (data.success && data.dataContent && data.dataContent.length > 0) {
        // Calculate total users from all user types
        const totalUsers = data.dataContent.reduce((sum, userType) => {
          return sum + parseInt(userType.totalUsers || 0);
        }, 0);
        
        setUserCount(totalUsers);
        console.log('ATARI Total Users set to:', totalUsers);
      } else {
        setUserCount(0);
        console.log('ATARI Dashboard API returned no data');
      }
    } catch (error) {
      console.error('Error fetching ATARI Dashboard data:', error);
      setUserCount(0);
    } finally {
      setLocalLoading(false);
    }
  };

  // Fetch user details for More Info
  const fetchUserDetails = async () => {
    try {
      setLocalLoading(true);
      console.log('=== ATARI Dashboard - Fetching User Details ===');
      
      const { data } = await axios.post(
        `${process.env.NODE_ENV === "development" ? process.env.REACT_APP_DEV_MODE : process.env.REACT_APP_PRO_MODE}/api/MT/ICAR`,
        {
          "flag": "Atari-2",
          "atariCode": parseInt(atariCode)
        },
        {
          headers: {
            'accept': '*/*',
            'Content-Type': 'application/json'
          }
        }
      );

      console.log('ATARI User Details API Response:', data);

      if (data.success && data.dataContent) {
        setUserDetails(Array.isArray(data.dataContent) ? data.dataContent : []);
      } else {
        setUserDetails([]);
      }
    } catch (error) {
      console.error('Error fetching ATARI user details:', error);
      setUserDetails([]);
    } finally {
      setLocalLoading(false);
    }
  };

  // Fetch demonstration count using ICAR_SurveyCount API with Atari_L flag
  const fetchDemonstrationCount = async () => {
    try {
      setLocalLoading(true);
      console.log('=== ATARI Dashboard - Fetching Demonstration Count ===');
      
      const { data } = await axios.post(
        `${process.env.NODE_ENV === "development" ? process.env.REACT_APP_DEV_MODE : process.env.REACT_APP_PRO_MODE}/api/MT/ICAR_SurveyCount`,
        {
          "flag": "Atari_L",
          "atariCode": parseInt(atariCode)
        },
        {
          headers: {
            'accept': '*/*',
            'Content-Type': 'application/json'
          }
        }
      );

      console.log('ATARI Demonstration Count API Response:', data);

      if (data.success && data.dataContent && data.dataContent.length > 0) {
        const totalCount = data.dataContent.reduce((sum, item) => {
          return sum + parseInt(item.total || item.count || 0, 10);
        }, 0);
        setDemonstrationCount(totalCount);
        console.log('ATARI Demonstration Count set to:', totalCount);
      } else {
        setDemonstrationCount(0);
        console.log('ATARI Demonstration Count API returned no data');
      }
    } catch (error) {
      console.error('Error fetching ATARI demonstration count:', error);
      setDemonstrationCount(0);
    } finally {
      setLocalLoading(false);
    }
  };

  // Fetch demonstration details for More Info using Atari_L1 flag
  const fetchDemonstrationDetails = async () => {
    try {
      setLocalLoading(true);
      console.log('=== ATARI Dashboard - Fetching Demonstration Details ===');
      
      const { data } = await axios.post(
        `${process.env.NODE_ENV === "development" ? process.env.REACT_APP_DEV_MODE : process.env.REACT_APP_PRO_MODE}/api/MT/ICAR_SurveyCount`,
        {
          "flag": "Atari_L1",
          "atariCode": parseInt(atariCode)
        },
        {
          headers: {
            'accept': '*/*',
            'Content-Type': 'application/json'
          }
        }
      );

      console.log('ATARI Demonstration Details API Response:', data);

      if (data.success && data.dataContent) {
        setDemonstrationDetails(Array.isArray(data.dataContent) ? data.dataContent : []);
      } else {
        setDemonstrationDetails([]);
      }
    } catch (error) {
      console.error('Error fetching ATARI demonstration details:', error);
      setDemonstrationDetails([]);
    } finally {
      setLocalLoading(false);
    }
  };

  // Handle More Info click
  const handleUserMoreInfo = async () => {
    if (activeTable === 'user') {
      setActiveTable('none');
      // Close drill-down tables when closing user table
      setShowDrillDown(false);
      setDrillDownData([]);
      setSelectedKvkCode(null);
    } else {
      setActiveTable('user');
      // Close any open drill-down tables from other sections
      setShowDemoDrillDown(false);
      setDemoDrillDownData([]);
      setSelectedDemoKvkCode(null);
      await fetchUserDetails();
      // Scroll to table
      setTimeout(() => {
        const tableElement = document.getElementById('atari-user-details-table');
        if (tableElement) {
          tableElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  };

  const handleDemonstrationMoreInfo = async () => {
    if (activeTable === 'demonstration') {
      setActiveTable('none');
      setDemonstrationDetails([]);
      setShowDemoDrillDown(false);
      setDemoDrillDownData([]);
      setSelectedDemoKvkCode(null);
    } else {
      setActiveTable('demonstration');
      // Close any open drill-down tables from user section
      setShowDrillDown(false);
      setDrillDownData([]);
      setSelectedKvkCode(null);
      await fetchDemonstrationDetails();
      // Scroll to table
      setTimeout(() => {
        const tableElement = document.getElementById('atari-demonstration-details-table');
        if (tableElement) {
          tableElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  };

  // Toggle drill-down data for KVK details
  const toggleDrillDownData = async (kvkName, kvkCode) => {
    // If the same KVK is clicked, toggle it closed
    if (showDrillDown && selectedKvkCode === kvkCode) {
      setShowDrillDown(false);
      setDrillDownData([]);
      setSelectedKvkCode(null);
      return;
    }

    // Otherwise, fetch and show the drill-down data
    try {
      setLocalLoading(true);
      console.log('=== ATARI Dashboard - Fetching Drill Down Data ===');
      
      const { data } = await axios.post(
        `${process.env.NODE_ENV === "development" ? process.env.REACT_APP_DEV_MODE : process.env.REACT_APP_PRO_MODE}/api/MT/ICAR`,
        {
          "flag": "Atari-3",
          "atariCode": parseInt(atariCode)
        },
        {
          headers: {
            'accept': '*/*',
            'Content-Type': 'application/json'
          }
        }
      );

      console.log('ATARI Drill Down API Response:', data);

      if (data.success && data.dataContent) {
        // Filter data for the selected KVK
        const filteredData = data.dataContent.filter(item => item.kvkCode === kvkCode);
        setDrillDownData(filteredData);
        setSelectedKvkCode(kvkCode);
        setShowDrillDown(true);
        
        // Scroll to drill-down table
        setTimeout(() => {
          const tableElement = document.getElementById('atari-drilldown-table');
          if (tableElement) {
            tableElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 100);
      } else {
        setDrillDownData([]);
      }
    } catch (error) {
      console.error('Error fetching ATARI drill-down data:', error);
      setDrillDownData([]);
    } finally {
      setLocalLoading(false);
    }
  };

  // Close drill-down table
  const closeDrillDown = () => {
    setShowDrillDown(false);
    setDrillDownData([]);
    setSelectedKvkCode(null);
  };

  // Toggle drill-down data for Demonstration details (KVK -> Details) using Atari_L2 flag
  const toggleDemoDrillDownData = async (kvkItem, kvkCode) => {
    // If the same KVK is clicked, toggle it closed
    if (showDemoDrillDown && selectedDemoKvkCode === kvkCode) {
      setShowDemoDrillDown(false);
      setDemoDrillDownData([]);
      setSelectedDemoKvkCode(null);
      return;
    }

    // Otherwise, fetch and show the drill-down data
    try {
      setLocalLoading(true);
      console.log('=== ATARI Dashboard - Fetching Demo Drill Down Data ===');
      
      const { data } = await axios.post(
        `${process.env.NODE_ENV === "development" ? process.env.REACT_APP_DEV_MODE : process.env.REACT_APP_PRO_MODE}/api/MT/ICAR_SurveyCount`,
        {
          "flag": "Atari_L2",
          "atariCode": parseInt(atariCode)
        },
        {
          headers: {
            'accept': '*/*',
            'Content-Type': 'application/json'
          }
        }
      );

      console.log('ATARI Demo Drill Down API Response:', data);

      if (data.success && data.dataContent) {
        setDemoDrillDownData(Array.isArray(data.dataContent) ? data.dataContent : []);
        setSelectedDemoKvkCode(kvkCode);
        setShowDemoDrillDown(true);
      } else {
        setDemoDrillDownData([]);
      }
    } catch (error) {
      console.error('Error fetching ATARI demo drill-down data:', error);
      setDemoDrillDownData([]);
    } finally {
      setLocalLoading(false);
    }
  };

  // Fetch dashboard data on component mount
  useEffect(() => {
    console.log('AtariDashboard component mounted');
    fetchAtariDashboardData();
  }, []);

  return (
    <>
      {/* Dashboard Cards */}
      <div className="col-xxl-6 col-xl-6">
        <div className="card info-card sales-card bg-gradient-danger card-img-holder">
          <div className="card-body">
            <img src={Circles} className="card-img-absolute" alt="circle-image" />
            <h5 className="card-title">Active User</h5>
            <div className="d-flex align-items-center">
              <div className="card-icon rounded-circle d-flex align-items-center justify-content-center">
                <i><FontAwesomeIcon icon={faUsers} /> </i>
              </div>
              <div className="ps-3">
                <h6 id="recordCount">{localLoading || loading ? 'Loading...' : userCount}</h6>
              </div>
            </div>
            <div className="mt-3">
              <button 
                className='btn btn-link p-0 text-white' 
                onClick={handleUserMoreInfo}
                title="Click for more information"
              >
                <i className="bi bi-arrow-right-circle-fill fs-6">More Info</i>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="col-xxl-6 col-xl-6">
        <div className="card info-card revenue-card bg-gradient-info card-img-holder">
          <div className="card-body">
            <img src={Circles} className="card-img-absolute" alt="circle-image" />
            <h5 className="card-title">Demonstration Machine</h5>
            <div className="d-flex align-items-center">
              <div className="card-icon rounded-circle d-flex align-items-center justify-content-center">
                <i><FontAwesomeIcon icon={faTractor} /> </i>
              </div>
              <div className="ps-3">
                <h6>{localLoading || loading ? 'Loading...' : demonstrationCount}</h6>
              </div>
            </div>
            <div className="mt-3">
              <button 
                className='btn btn-link p-0 text-white' 
                onClick={handleDemonstrationMoreInfo}
                title="Click for more information"
              >
                <i className="bi bi-arrow-right-circle-fill fs-6">More Info</i>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* User Details Table */}
      {activeTable === 'user' && (
        <div className="col-lg-12" id="atari-user-details-table">
          <div className="card">
            <div className="card-body">
              <h5 className="card-title1">ATARI Active Users Details</h5>
              <div className="d-flex justify-content-end mb-2">
                <button 
                  className="btn btn-sm btn-success" 
                  onClick={() => exportToCSV(userDetails, "ATARI_Active_Users_Report", ["S.No", "KVK Name", "Count"])}
                >
                  Export CSV
                </button>
              </div>
              <div className="table-responsive">
                <table className="table table-striped">
                  <thead>
                    <tr>
                      <th>Sr. No.</th>
                      <th>KVK Name</th>
                      <th>Count</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {userDetails && userDetails.length > 0 ? (
                      userDetails.map((user, index) => (
                        <tr key={index}>
                          <td>{index + 1}</td>
                          <td>{user.kvK_Name}</td>
                          <td>{user.totalUsers}</td>
                          <td>
                            <span
                              role="button"
                              onClick={() => toggleDrillDownData(user.kvK_Name, user.kvkCode)}
                              style={{
                                cursor: "pointer",
                                color: "#0d6efd",
                                fontSize: "16px",
                                fontWeight: "bold"
                              }}
                              title={showDrillDown && selectedKvkCode === user.kvkCode ? "Close Details" : "View Details"}
                            >
                              {showDrillDown && selectedKvkCode === user.kvkCode ? "-" : "+"}
                            </span>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={4} className="text-center">No user data available</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Drill-Down Table for KVK Details */}
      {showDrillDown && drillDownData.length > 0 && (
        <div className="col-lg-12" id="atari-drilldown-table">
          <div className="card">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="card-title1 mb-0">User Role Details - {drillDownData[0]?.kvK_Name}</h5>
                <button 
                  className="btn btn-sm btn-secondary" 
                  onClick={closeDrillDown}
                >
                  <i className="bi bi-x-circle"></i> Close
                </button>
              </div>
              <div className="d-flex justify-content-end mb-2">
                <button 
                  className="btn btn-sm btn-success" 
                  onClick={() => exportToCSV(drillDownData, `${drillDownData[0]?.kvK_Name}_User_Roles_Report`, ["S.No", "User Role Name", "Total Users"])}
                >
                  Export CSV
                </button>
              </div>
              <div className="table-responsive">
                <table className="table table-striped table-bordered">
                  <thead className="table-light">
                    <tr>
                      <th>S.No</th>
                      <th>User Role Name</th>
                      <th>Total Users</th>
                    </tr>
                  </thead>
                  <tbody>
                    {drillDownData && drillDownData.length > 0 ? (
                      drillDownData.map((item, index) => (
                        <tr key={index}>
                          <td>{index + 1}</td>
                          <td>{item.userRoleName}</td>
                          <td>{item.totalUsers}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={3} className="text-center">No data available</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Demonstration Details Table */}
      {activeTable === 'demonstration' && (
        <div className="col-lg-12" id="atari-demonstration-details-table">
          <div className="card">
            <div className="card-body">
              <h5 className="card-title1">ATARI Demonstration Details</h5>
              <div className="d-flex justify-content-end mb-2">
                <button 
                  className="btn btn-sm btn-success" 
                  onClick={() => exportToCSV(demonstrationDetails, "ATARI_Demonstration_KVK_Report", ["S.No", "KVK Name", "Count"])}
                >
                  Export CSV
                </button>
              </div>
              <div className="table-responsive">
                <table className="table table-striped">
                  <thead>
                    <tr>
                      <th>Sr. No.</th>
                      <th>Atari Name</th>
                      <th>Count</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {demonstrationDetails && demonstrationDetails.length > 0 ? (
                      demonstrationDetails.map((demo, index) => (
                        <React.Fragment key={index}>
                          <tr>
                            <td>{index + 1}</td>
                            <td>{demo.atari_Name}</td>
                            <td>{demo.total}</td>
                            <td>
                              <span
                                role="button"
                                onClick={() => toggleDemoDrillDownData(demo, demo.kvkCode)}
                                style={{
                                  cursor: "pointer",
                                  color: "#0d6efd",
                                  fontSize: "16px",
                                  fontWeight: "bold"
                                }}
                                title={showDemoDrillDown && selectedDemoKvkCode === demo.kvkCode ? "Close Details" : "View Details"}
                              >
                                {showDemoDrillDown && selectedDemoKvkCode === demo.kvkCode ? "-" : "+"}
                              </span>
                            </td>
                          </tr>
                          {showDemoDrillDown && selectedDemoKvkCode === demo.kvkCode && (
                            <tr>
                              <td colSpan="4">
                                {localLoading ? (
                                  <div className="text-center py-4">
                                    <div className="spinner-border text-primary" role="status">
                                      <span className="visually-hidden">Loading...</span>
                                    </div>
                                    <p className="mt-2 text-muted">Loading details...</p>
                                  </div>
                                ) : demoDrillDownData.length > 0 ? (
                                  <>
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                      <h5 className="mb-0">Details for {demo.atari_Name}</h5>
                                      <button 
                                        className="btn btn-sm btn-outline-success" 
                                        onClick={() => exportToCSV(demoDrillDownData, `ATARI_Details_${demo.kvK_Name || demo.name}`, ["S.No", "Detail", "Count"])}
                                      >
                                        Export CSV
                                      </button>
                                    </div>
                                    <table className="table table-bordered">
                                      <thead>
                                        <tr>
                                          <th>Sr. No.</th>
                                          <th>KVK Name</th>
                                          <th>Count</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {demoDrillDownData.map((detail, dIndex) => (
                                          <tr key={dIndex}>
                                            <td>{dIndex + 1}</td>
                                            
                                            <td>{detail.kvK_Name}</td>
                                            <td>{detail.total}</td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </>
                                ) : (
                                  <div className="text-center py-3 text-muted">
                                    <p>No details available</p>
                                  </div>
                                )}
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={4} className="text-center">No demonstration data available</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AtariDashboard;
